/* TRUCK QR — a permanent tracker that follows ONE truck, separate from the pump
   company's own link. Hand it to the pump crew paired with that truck: same QR every
   day, no matter whose job it's on. Reset it if the truck changes hands. */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { printQrPage } from "@/lib/printQr";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Printer, RotateCcw, QrCode } from "lucide-react";
import { PreviewLinkWarning } from "@/components/PreviewLinkWarning";

export const TruckQRDialog = ({ open, onOpenChange, truck }) => {
  const [token, setToken] = useState("");
  const url = token ? `${window.location.origin}/truck/${token}` : "";

  const mint = async (reset = false) => {
    try {
      const { data } = await api.post(`/trucks/${truck.id}/track-token`, { reset });
      setToken(data.track_token);
      if (reset) toast.success("New link issued — the old QR no longer works");
    } catch { toast.error("Could not create the tracker link"); }
  };

  useEffect(() => { if (open && truck) { setToken(""); mint(false); } /* eslint-disable-next-line */ }, [open, truck?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="truck-qr-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2"><QrCode className="w-4 h-4 text-primary-orange" /> {truck?.number} · Truck tracker</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-industrial">
          Permanent QR for this truck. The pump crew scans it once and sees every load this truck
          runs today — site, customer, m³, mix, live position and ETA.
        </p>
        <PreviewLinkWarning what="QR code" />
        {url && (
          <div className="bg-white p-4 flex items-center justify-center">
            <QRCodeSVG id="truck-qr-svg" value={url} size={220} level="M" includeMargin={false} />
          </div>
        )}
        <div className="mono text-[11px] break-all border border-industrial px-2 py-2" data-testid="truck-qr-url">{url || "…"}</div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="truck-qr-copy" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
          <Button variant="outline" data-testid="truck-qr-print" onClick={() => printQrPage({
            title: `Truck Tracker · ${truck?.number || ""}`,
            tagLine: "ARMORBATCH GLOBAL · Truck Tracker",
            heading: truck?.number || "",
            description: "Scan with your phone camera to follow this truck all day — every load, live GPS, ETA and volume. The link is permanent, so bookmark it.",
            footer: "© ARMORBATCH GLOBAL · Live truck tracker",
            url, svgElementId: "truck-qr-svg",
          })} className="rounded-none uppercase tracking-widest text-xs font-bold border-industrial"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
          <Button variant="outline" data-testid="truck-qr-reset" onClick={() => mint(true)}
            title="Issue a new link — use this if the truck changes hands"
            className="rounded-none uppercase tracking-widest text-xs font-bold border-industrial hover:border-red-500 hover:text-red-400 ml-auto"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset link</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
